import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Configure DOMPurify hooks to strip styling (except on blockquotes)
const configureDOMPurify = () => {
  // Filter attributes during sanitization
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    // Remove style attribute - but allow on blockquote for quote styling
    if (data.attrName === 'style') {
      if (node.nodeName !== 'BLOCKQUOTE') {
        data.keepAttr = false;
      }
    }
    // Filter class attribute - only keep quote-related classes for toggle functionality
    if (data.attrName === 'class') {
      const classValue = data.attrValue || '';
      const allowedClasses = [
        'gmail_quote',
        'gmail_extra',
        'yahoo_quoted',
        'OutlookMessageHeader',
        'moz-cite-prefix',
      ];
      const filteredClasses = classValue
        .split(' ')
        .filter((c) => allowedClasses.includes(c))
        .join(' ');
      if (filteredClasses) {
        data.attrValue = filteredClasses;
      } else {
        data.keepAttr = false;
      }
    }
  });
};

// Initialize hooks once
let hooksConfigured = false;
const ensureHooksConfigured = () => {
  if (!hooksConfigured) {
    configureDOMPurify();
    hooksConfigured = true;
  }
};

export default function EmailMessage({ content, content_text }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(100);
  const [iframeReady, setIframeReady] = useState(false);
  const iframeRef = useRef(null);

  // Detect dark mode from the app's root element
  const isDarkMode = document.documentElement.classList.contains('dark');

  // Sanitize HTML content using DOMPurify - strips all inline styles and fonts
  const sanitizedContent = useMemo(() => {
    if (!content) return content_text || '';

    ensureHooksConfigured();

    return DOMPurify.sanitize(content, {
      // Allowed structural tags only - no <style> or <font>
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        'a',
        'ul',
        'ol',
        'li',
        'img',
        'table',
        'thead',
        'tbody',
        'tr',
        'td',
        'th',
        'div',
        'span',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'pre',
        'code',
        'hr',
      ],
      // Safe attributes - style is filtered by hook (only allowed on blockquote)
      ALLOWED_ATTR: [
        'href',
        'src',
        'alt',
        'title',
        'target',
        'rel',
        'id',
        'class', // Filtered by hook to only allow quote-related classes
        'style', // Filtered by hook to only allow on blockquote
        'colspan',
        'rowspan',
        'width',
        'height', // For tables/images - applied via attributes not CSS
      ],
      // Additional security settings
      FORBID_TAGS: ['style', 'script', 'font', 'center', 'marquee', 'blink'],
      FORBID_ATTR: ['bgcolor', 'background', 'color', 'face', 'size'],
    });
  }, [content, content_text]);

  // Check if content has quoted text
  const hasQuotedText = useMemo(() => {
    if (!content) return false;

    // Common patterns for quoted text
    const quotedPatterns = [
      'gmail_quote',
      'gmail_extra',
      'yahoo_quoted',
      'OutlookMessageHeader',
      'moz-cite-prefix',
      // Also check for common text patterns
      /^On .* wrote:/m,
      /^From:.*\nSent:/m,
      /^&gt;/m, // Email quote markers
    ];

    return quotedPatterns.some((pattern) => {
      if (typeof pattern === 'string') {
        return content.includes(pattern);
      }
      return pattern.test(content);
    });
  }, [content]);

  // Build the full HTML document for the iframe (stable - doesn't depend on showQuoted)
  const iframeSrcDoc = useMemo(() => {
    if (!sanitizedContent) return '';

    // Theme-aware colors
    const textColor = isDarkMode ? '#e5e7eb' : '#1f2937';
    const linkColor = isDarkMode ? '#60a5fa' : '#2563eb';

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap">
          <style>
            * {
              box-sizing: border-box;
            }
            body {
              font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              margin: 0;
              padding: 0;
              color: ${textColor};
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            a {
              color: ${linkColor};
              text-decoration: underline;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            table {
              max-width: 100%;
              border-collapse: collapse;
            }
            p {
              margin: 0 0 0.5em 0;
            }
            /* Quoted text elements - controlled via JS */
            .gmail_quote, .gmail_extra, .yahoo_quoted, 
            .OutlookMessageHeader, .moz-cite-prefix, blockquote {
              transition: none;
            }
            .hide-quoted .gmail_quote, 
            .hide-quoted .gmail_extra, 
            .hide-quoted .yahoo_quoted, 
            .hide-quoted .OutlookMessageHeader, 
            .hide-quoted .moz-cite-prefix, 
            .hide-quoted blockquote {
              display: none !important;
            }
          </style>
        </head>
        <body class="hide-quoted">${sanitizedContent}</body>
      </html>
    `;
  }, [sanitizedContent, isDarkMode]);

  // Toggle quoted text visibility by manipulating iframe body class directly (no reload)
  useEffect(() => {
    if (!iframeReady) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc?.body) {
        if (showQuoted) {
          doc.body.classList.remove('hide-quoted');
        } else {
          doc.body.classList.add('hide-quoted');
        }
        // Recalculate height after toggling
        setTimeout(() => {
          setIframeHeight(doc.body.scrollHeight + 10);
        }, 10);
      }
    } catch (e) {
      console.warn('Could not toggle quoted text:', e);
    }
  }, [showQuoted, iframeReady]);

  // Resize iframe to fit content
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc?.body) {
        // Give a small delay for fonts to load and content to render
        setTimeout(() => {
          const height = doc.body.scrollHeight;
          setIframeHeight(height + 10); // Add small buffer
          setIframeReady(true);
        }, 100);
      }
    } catch (e) {
      // Cross-origin issues shouldn't happen with srcdoc, but handle gracefully
      console.warn('Could not access iframe content:', e);
    }
  }, []);

  return (
    <div className='text-left text-sm'>
      {content && sanitizedContent ? (
        <>
          <iframe
            ref={iframeRef}
            srcDoc={iframeSrcDoc}
            className='w-full border-0'
            style={{ height: `${iframeHeight}px` }}
            scrolling='no'
            onLoad={handleIframeLoad}
            title='Email content'
            sandbox='allow-same-origin allow-popups allow-popups-to-escape-sandbox'
          />
          {hasQuotedText && (
            <button
              onClick={() => setShowQuoted(!showQuoted)}
              className='mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
            >
              {showQuoted ? (
                <>
                  <ChevronUp className='h-3 w-3' />
                  Hide quoted text
                </>
              ) : (
                <>
                  <ChevronDown className='h-3 w-3' />
                  Show quoted text
                </>
              )}
            </button>
          )}
        </>
      ) : (
        <p className='whitespace-pre-wrap break-words text-muted-foreground'>
          {content_text || ''}
        </p>
      )}
    </div>
  );
}
